import { useState, type FormEvent } from "react";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FieldHint } from "@/components/ui/field-hint";
import { FIELD_HINTS } from "@/lib/help-text";

interface AddOrgDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    slug: string;
    ownerName?: string;
    ownerEmail?: string;
  }) => Promise<string | void>;
}

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function AddOrgDialog({ open, onClose, onSubmit }: AddOrgDialogProps) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManual, setSlugManual] = useState(false);
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleNameChange(value: string) {
    setName(value);
    if (!slugManual) {
      setSlug(toSlug(value));
    }
  }

  function handleSlugChange(value: string) {
    setSlugManual(true);
    setSlug(toSlug(value));
  }

  function reset() {
    setName("");
    setSlug("");
    setSlugManual(false);
    setOwnerName("");
    setOwnerEmail("");
    setError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) {
      setError("Organization name and slug are required.");
      return;
    }
    // If one owner field is filled, both must be filled
    const hasOwnerName = ownerName.trim().length > 0;
    const hasOwnerEmail = ownerEmail.trim().length > 0;
    if (hasOwnerName !== hasOwnerEmail) {
      setError("Please provide both owner name and email, or leave both blank.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        slug,
        ...(hasOwnerName && hasOwnerEmail
          ? { ownerName: ownerName.trim(), ownerEmail: ownerEmail.trim() }
          : {}),
      });
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create organization.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!submitting) {
          reset();
          onClose();
        }
      }}
    >
      <DialogHeader>
        <DialogTitle>Add Organization</DialogTitle>
        <DialogDescription>
          Create a new client organization.
        </DialogDescription>
      </DialogHeader>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          id="org-name"
          label="Organization Name"
          placeholder="Acme, Inc."
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          required
          disabled={submitting}
        />

        <div>
          <Input
            id="org-slug"
            label="Slug"
            placeholder="acme-inc"
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            required
            disabled={submitting}
          />
          <FieldHint>{FIELD_HINTS.orgSlug}</FieldHint>
        </div>

        <div className="border-t border-border pt-4">
          <p className="mb-1 font-body text-sm font-medium text-text">
            Owner (Optional)
          </p>
          <p className="mb-3 font-body text-xs text-text-muted">
            If provided, the owner will receive login credentials via email.
          </p>

          <div className="space-y-4">
            <Input
              id="org-owner-name"
              label="Owner Name"
              placeholder="Jane Smith"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              disabled={submitting}
            />

            <Input
              id="org-owner-email"
              label="Owner Email"
              type="email"
              placeholder="jane@acme.com"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              disabled={submitting}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Creating..." : "Create Organization"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
